/**
 * control-classes.ts — JEDNA miara dla wszystkich kontrolek w wierszu filtrów
 * `ConfigurableTable` (pola tekstowe/liczbowe, dropdowny „Wszystkie", wybór daty,
 * chip aktywnego filtra).
 *
 * Powód istnienia: wiersz filtrów ma DWIE ścieżki renderowania — nowszą, opartą na
 * prymitywach biblioteki UI (`filter-row-controls.tsx`: `Input`/`Dropdown`, radius
 * `rounded-lg`, `py-2`) i starszą, z surowym `<input>` i własnymi klasami
 * (`FilterRowInputControl.tsx`: radius `rounded`, `py-1`). Efekt w jednym wierszu:
 * pola tekstowe niższe i o ciaśniejszym zaokrągleniu niż stojący obok dropdown.
 * Zamiast poprawiać każde miejsce osobno (i rozjechać je ponownie przy następnej
 * zmianie) trzymamy wysokość i radius TUTAJ i importujemy w obu ścieżkach.
 *
 * `h-9` (36 px) — wysokość pośrednia między dawnymi 24 px pola tekstowego a 38 px
 * dropdowna: wiersz filtrów nie puchnie, a kontrolki są wygodne w kliknięciu.
 * `rounded-lg` (8 px) — wyraźniejsze zaokrąglenie, spójne z formularzami w całej apce.
 */

/** Wysokość + zaokrąglenie + rozmiar tekstu — wspólne dla KAŻDEJ kontrolki filtra.
 * `h-8` (32 px) + `rounded-md` (6 px) = domyślny rozmiar kontrolki Ant Design (redesign
 * „Ant" wg Figmy: wiersz wyszukiwarek to niskie, delikatnie zaokrąglone pola). */
export const FILTER_CONTROL_SIZE_CLASS = 'h-8 rounded-full text-xs';

/**
 * Wariant dla kontrolek, które same nie rysują obramowania i tła (surowy `<input>`
 * w starszej ścieżce) — dokłada je z tokenów, żeby wyglądały jak prymityw `Input`.
 */
export const FILTER_CONTROL_INPUT_CLASS = [
  FILTER_CONTROL_SIZE_CLASS,
  'min-w-0 flex-1 border bg-bg-primary px-2 text-text-primary placeholder:text-text-muted',
  'border-border-primary transition-colors',
  'focus:border-transparent focus:outline-none focus:ring-2 focus:ring-accent-500',
].join(' ');

/**
 * Wariant `FILTER_CONTROL_INPUT_CLASS` z miejscem na ikonę po lewej (np. lupa
 * zamiast tekstu placeholdera) — `w-full` zamiast `flex-1` (ten input siedzi w
 * owijającym go `div.relative`, który sam jest elementem flex; input już nie
 * jest bezpośrednim dzieckiem wiersza) + `pl-9` nadpisujące lewy padding z
 * `px-2` (Tailwind porządkuje longhand nad shorthand tej samej osi, więc to
 * bezpieczne nadpisanie samej lewej strony — mirror `Input.tsx`: `px-3` + `pl-10`).
 * `pl-9` (36px), nie `pl-7` — ikona zaczyna się na `pl-2` (8px) i ma 14px
 * szerokości (kończy się na 22px); `pl-9` zostawia ~14px odstępu do tekstu,
 * tej samej proporcji co `Input.tsx` (tam ikona kończy się na 26px, pole
 * zaczyna na 40px) — przy węższej kolumnie liczbowej (kolumna z jedną cyfrą,
 * patrz `size={1}` w `FilterRowInputControl.tsx`) mniejszy odstęp wizualnie
 * zlewał się z ikoną.
 */
export const FILTER_CONTROL_INPUT_WITH_ICON_CLASS = [
  FILTER_CONTROL_SIZE_CLASS,
  'w-full border bg-bg-primary px-2 pl-9 text-text-primary placeholder:text-text-muted',
  'border-border-primary transition-colors',
  'focus:border-transparent focus:outline-none focus:ring-2 focus:ring-accent-500',
].join(' ');
